/**
 * This method will save store data in local storage
 * @param item
 * @param data
 */
export const saveItemInLocalStorage = (item: string, data: unknown): void => {
  try {
    const serializedState = JSON.stringify(data);
    localStorage.setItem(item, serializedState);
  } catch (error) {
    console.error(`Failed to save item "${item}" to localStorage:`, error);
  }
};

/**
 * Use this method to read store data from local storage
 * @param item
 * @returns parsed value or undefined if missing/corrupt
 */
export const readItemFromLocalStorage = <T = unknown,>(
  item: string,
): T | undefined => {
  try {
    const serializedState = localStorage.getItem(item);
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState) as T;
  } catch (error) {
    console.error(`Failed to read item "${item}" from localStorage:`, error);
    return undefined;
  }
};

/**
 * Use this method to read a cookie value by key
 * @param cookiesKey
 * @returns decoded cookie value or undefined if not found
 */
export const getWebCookies = (cookiesKey: string): string | undefined => {
  const cookies: Record<string, string> = document.cookie
    .split(';')
    .map((cookie) => cookie.split('='))
    .reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key.trim()]: value ? decodeURIComponent(value.trim()) : '',
      }),
      {},
    );

  return cookies[cookiesKey] ?? undefined;
};

const VALID_COOKIE_KEY_REGEX = /^[a-zA-Z0-9_-]+$/;

/**
 * Use this method to set cookies data in cookies storage
 * @param cookiesKey
 * @param cookiesValue
 * @param expiry - Date object, valid date string, or null for session cookie
 * @param cookiesPath
 * @param sameSite
 */
export const setCookies = (
  cookiesKey: string,
  cookiesValue: string,
  expiry: Date | string | null = null,
  cookiesPath = '/',
  sameSite: 'Strict' | 'Lax' | 'None' = 'Strict',
): void => {
  if (!VALID_COOKIE_KEY_REGEX.test(cookiesKey)) {
    throw new Error(`Invalid cookie key: "${cookiesKey}"`);
  }

  const encodedValue = encodeURIComponent(cookiesValue);

  let expirySegment = '';
  if (expiry instanceof Date) {
    expirySegment = `;expires=${expiry.toUTCString()}`;
  } else if (typeof expiry === 'string' && expiry.trim() !== '') {
    expirySegment = `;expires=${expiry}`;
  }

  document.cookie = `${cookiesKey}=${encodedValue}${expirySegment};path=${cookiesPath};Secure;SameSite=${sameSite}`;
};

/**
 * Use this method to get a cookie value by key
 * @param cookiesKey
 * @returns decoded cookie value or null if not found
 */
export const getCookies = (cookiesKey: string): string | null => {
  const match = document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('='))
    .find(([key]) => key === cookiesKey);

  if (!match || match.length < 2) {
    return null;
  }

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
};

/**
 * Use this method to delete a cookie by key
 * @param cookiesKey
 * @param cookiesPath
 */
export const deleteCookies = (cookiesKey: string, cookiesPath = '/'): void => {
  if (!VALID_COOKIE_KEY_REGEX.test(cookiesKey)) {
    throw new Error(`Invalid cookie key: "${cookiesKey}"`);
  }

  document.cookie = `${cookiesKey}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=${cookiesPath};Secure;SameSite=Strict`;
};
